import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, AlertTriangle, Trash2, X, MessageCircle, Settings as SettingsIcon } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import AdminWhatsAppTab from '../components/AdminWhatsAppTab';

import { API_BASE, apiFetch } from '../lib/api';

interface SettingItem {
  value: string;
  description: string;
}

const defaultSettings: Record<string, SettingItem> = {
  site_name: { value: '我的商店', description: '網站名稱' },
  business_phone: { value: '85251164453', description: 'WhatsApp 業務電話' },
  admin_phone: { value: '85251164453', description: '管理員通知電話' },
  bank_account: { value: 'DBS A/C - 016-000227829', description: '銀行帳號' },
  fps_id: { value: 'FPS - 108810334', description: 'FPS 轉數快 ID' },
  company_name: { value: 'DELICIOUS EXPRESS LTD', description: '公司名稱' },
  free_shipping_threshold: { value: '200', description: '免運門檻（HKD，例如 200）' },
  shipping_above_threshold_label: { value: '免運費', description: '達到免運門檻時顯示的運費說明' },
  shipping_below_threshold_label: { value: '順豐到付（滿 HK$200 包郵）', description: '未達免運門檻時顯示的運費說明' },
  hong_kong_spicy_valley_gift_code: { value: '', description: '專屬優惠碼（可留空）' },
};

type DangerTarget = 'orders' | 'customers' | 'referrals';

interface DangerActionDef {
  key: DangerTarget;
  label: string;
  description: string;
  confirmPhrase: string;
  endpoint: string;
}

const DANGER_ACTIONS: DangerActionDef[] = [
  {
    key: 'orders',
    label: '清除所有訂單',
    description: '永久刪除 order_records 內所有訂單記錄，無法復原。',
    confirmPhrase: 'DELETE_ORDERS',
    endpoint: '/api/admin/data/orders',
  },
  {
    key: 'customers',
    label: '清除所有客戶資料',
    description: '永久刪除 member_profiles 內所有客戶資料，無法復原。',
    confirmPhrase: 'DELETE_CUSTOMERS',
    endpoint: '/api/admin/data/customers',
  },
  {
    key: 'referrals',
    label: '清除所有推薦資料',
    description: '永久刪除 referrers 與相關推薦設定，無法復原。',
    confirmPhrase: 'DELETE_REFERRALS',
    endpoint: '/api/admin/data/referrals',
  },
];

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Record<string, SettingItem>>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [currentRole, setCurrentRole] = useState<string>('');
  const [dangerAction, setDangerAction] = useState<DangerActionDef | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [dangerLoading, setDangerLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'whatsapp'>('general');

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    const storedUser = localStorage.getItem('admin_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentRole(user?.role || '');
      } catch {
        setCurrentRole('');
      }
    }
    fetchSettings();
  }, [token, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        const merged = { ...defaultSettings };
        for (const [key, item] of Object.entries(data)) {
          if (key.startsWith('whatsapp_')) continue;
          if (merged[key]) {
            merged[key] = { ...merged[key], value: (item as any).value || item };
          } else {
            merged[key] = { value: (item as any).value || item, description: (item as any).description || '' };
          }
        }
        delete (merged as any).min_days_advance;
        setSettings(merged);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取設置失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: Record<string, any> = {};
      for (const [key, item] of Object.entries(settings)) {
        payload[key] = { value: item.value, description: item.description };
      }

      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccess('設置已保存！');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('保存失敗');
      }
    } catch (e) {
      setError('保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const canUseDangerZone = currentRole === 'super_admin' || currentRole === 'admin';

  const openDangerAction = (action: DangerActionDef) => {
    if (!canUseDangerZone) return;
    setDangerAction(action);
    setConfirmText('');
    setError('');
  };

  const closeDangerAction = () => {
    setDangerAction(null);
    setConfirmText('');
  };

  const executeDangerAction = async () => {
    if (!dangerAction) return;
    if (confirmText !== dangerAction.confirmPhrase) {
      setError(`確認碼不正確，請輸入「${dangerAction.confirmPhrase}」`);
      return;
    }
    setDangerLoading(true);
    setError('');
    try {
      const res = await apiFetch(dangerAction.endpoint, { method: 'DELETE' });
      if (res.ok) {
        setSuccess(`已執行：${dangerAction.label}`);
        closeDangerAction();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `執行 ${dangerAction.label} 失敗`);
      }
    } catch (e) {
      setError('網絡錯誤，操作未成功');
    } finally {
      setDangerLoading(false);
    }
  };

  return (
    <AdminLayout currentPage="settings-basic">
      <div>
        {/* Tab bar */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${activeTab === 'general' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <SettingsIcon size={16} /> 系統設置
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${activeTab === 'whatsapp' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <MessageCircle size={16} /> WhatsApp 連線
          </button>
        </div>

        {activeTab === 'whatsapp' ? (
          <AdminWhatsAppTab />
        ) : (
        <>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">系統設置</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(settings).map(([key, item]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {item.description}
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">({key})</span>
                  </label>
                  <input
                    value={item.value}
                    onChange={e => updateSetting(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-gray-800 dark:text-white"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 font-medium"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? '保存中...' : '保存設置'}
              </button>
            </div>
          </div>
        )}

        {/* Danger zone */}
        <div className="mt-8 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h3 className="text-lg font-bold text-red-800 dark:text-red-200">危險刪除區</h3>
          </div>
          <p className="text-sm text-red-700 dark:text-red-300 mb-4">
            以下操作會永久刪除資料，無法復原。僅供管理員使用。
          </p>

          {!canUseDangerZone && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm rounded-lg">
              你目前的權限無法使用危險刪除功能。
            </div>
          )}

          <div className="space-y-3">
            {DANGER_ACTIONS.map((action) => (
              <div
                key={action.key}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg p-4"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{action.label}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{action.description}</div>
                </div>
                <button
                  onClick={() => openDangerAction(action)}
                  disabled={!canUseDangerZone}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Trash2 size={16} /> 刪除
                </button>
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Danger confirmation modal */}
      {dangerAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertTriangle size={22} />
                <h3 className="text-lg font-bold">確認{dangerAction.label}</h3>
              </div>
              <button onClick={closeDangerAction} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={22} />
              </button>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              你即將{dangerAction.label}。此操作不可復原，請在下方輸入確認碼：
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 mb-4 text-center font-mono text-gray-900 dark:text-white select-all">
              {dangerAction.confirmPhrase}
            </div>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`輸入「${dangerAction.confirmPhrase}」以確認`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white mb-6"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeDangerAction}
                disabled={dangerLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                取消
              </button>
              <button
                onClick={executeDangerAction}
                disabled={dangerLoading || confirmText !== dangerAction.confirmPhrase}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {dangerLoading ? '執行中...' : <><Trash2 size={16} /> 確認刪除</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSettings;
